import React from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  getMuiLocalizationProviderAdapterName,
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import {
  OrderItemAction,
  OrderUserSchedulePickupActionsProps,
  ShippingStatus,
} from "./Order.types";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en"; // US
import "dayjs/locale/en-gb"; // UK
import "dayjs/locale/en-ca"; // CA

const useStyles = (theme: Theme) => ({
  OrderActionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  OrderUserSchedulePickupActionButtonUnselected: {
    muiColor: "primary",
    muiVariant: "contained",
    width: "240px",
  },
  OrderUserSchedulePickupActionButtonSelected: {
    muiColor: "primary",
    muiVariant: "outlined",
    width: "240px",
  },
  OrderActionRowContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "center",
  },
  OrderActionSingleItemRowContainer: {
    padding: 1,
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  OrderActionContentShortTextFieldContainer: {
    padding: 1,
  },
  OrderActionContentShortTextField: {
    width: "240px",
  },
  OrderActionRightAlignedRowContainer: {
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  OrderUserSchedulePickupActionButtonContainer: {
    pt: 2,
    pb: 1,
  },
  OrderActionContentFullWidthTextFieldContainer: {
    width: "100%",
  },
  OrderActionContentFullWidthTextField: {
    width: "100%",
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
});

export const OrderUserSchedulePickupAction: React.FC<
  OrderUserSchedulePickupActionsProps
> = (props): React.JSX.Element => {
  const {
    originalStatus,
    itemLabel,
    orderItemAction,
    multipleItmes = false,
    onOrderItemActionUpdate,
    styles,
  } = props;

  logDebug("OrderUserSchedulePickupAction PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("OrderUserSchedulePickupAction effectiveStyles");
  logDebug(effectiveStyles);

  const handleNoteValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.note = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleUseTheSameDateValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.useTheSameDateAndTime =
      !newOrderItemAction.useTheSameDateAndTime;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const onPickupDateChange = (event: any) => {
    if (event) {
      if (event instanceof dayjs) {
        const newValue = (event as Dayjs).toDate();
        let newOrderItemAction: OrderItemAction =
          packableDeepClone(orderItemAction);
        newOrderItemAction.actionDate = newValue;
        onOrderItemActionUpdate(newOrderItemAction);
      }
    }
  };

  const handleSchedulePickupClick = (event: React.MouseEvent<HTMLElement>) => {
    let newOrderItemAction: OrderItemAction =
      packableDeepClone(orderItemAction);
    if (newOrderItemAction.shippingStatus === ShippingStatus.PICKUP_REQUESTED) {
      newOrderItemAction.shippingStatus = originalStatus;
    } else {
      newOrderItemAction.shippingStatus = ShippingStatus.PICKUP_REQUESTED;
    }
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const minDate = dayjs();
  const maxDate = dayjs().add(7, "days");
  const startTime = dayjs().set("hour", 8).set("minute", 0); // 08:00 AM
  const endTime = dayjs().set("hour", 17).set("minute", 0); // 05:00 PM

  return (
    <>
      <Grid sx={effectiveStyles.OrderActionContainer}>
        <Grid
          className="OrderActionSingleItemRowContainer"
          sx={effectiveStyles.OrderActionSingleItemRowContainer}
        >
          <Grid
            className="OrderUserSchedulePickupActionButtonContainer"
            sx={effectiveStyles.OrderUserSchedulePickupActionButtonContainer}
          >
            <Button
              className={
                orderItemAction.shippingStatus ===
                ShippingStatus.PICKUP_REQUESTED
                  ? "OrderUserSchedulePickupActionButtonSelected"
                  : "OrderUserSchedulePickupActionButtonUnselected"
              }
              sx={
                orderItemAction.shippingStatus ===
                ShippingStatus.PICKUP_REQUESTED
                  ? effectiveStyles.OrderUserSchedulePickupActionButtonSelected
                  : effectiveStyles.OrderUserSchedulePickupActionButtonUnselected
              }
              disabled={
                orderItemAction.shippingStatus ===
                ShippingStatus.CANCEL_REQUESTED
              }
              onClick={handleSchedulePickupClick}
              variant={
                orderItemAction.shippingStatus ===
                ShippingStatus.PICKUP_REQUESTED
                  ? effectiveStyles.OrderUserSchedulePickupActionButtonSelected
                      .muiVariant
                  : effectiveStyles
                      .OrderUserSchedulePickupActionButtonUnselected.muiVariant
              }
              color={
                orderItemAction.shippingStatus ===
                ShippingStatus.PICKUP_REQUESTED
                  ? effectiveStyles.OrderUserSchedulePickupActionButtonSelected
                      .muiColor
                  : effectiveStyles
                      .OrderUserSchedulePickupActionButtonUnselected.muiColor
              }
            >
              {orderItemAction.shippingStatus ===
              ShippingStatus.PICKUP_REQUESTED
                ? `Undo Pickup Request ${itemLabel}`
                : `Request Pickup ${itemLabel}`}
            </Button>
          </Grid>
        </Grid>
        {orderItemAction.shippingStatus === ShippingStatus.PICKUP_REQUESTED && (
          <>
            {multipleItmes && (
              <Grid
                className="OrderActionSingleItemRowContainer"
                sx={effectiveStyles.OrderActionSingleItemRowContainer}
              >
                <Grid
                  className="OrderActionContentFullWidthTextFieldContainer"
                  sx={
                    effectiveStyles.OrderActionContentFullWidthTextFieldContainer
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={orderItemAction.useTheSameDateAndTime}
                        onChange={handleUseTheSameDateValueChange}
                      />
                    }
                    label="Use the same Date and Time for all items"
                  />
                </Grid>
              </Grid>
            )}
            <Grid
              className="OrderActionRowContainer"
              sx={effectiveStyles.OrderActionRowContainer}
            >
              <Grid
                className="OrderActionContentShortTextFieldContainer"
                sx={effectiveStyles.OrderActionContentShortTextFieldContainer}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={getMuiLocalizationProviderAdapterName()}
                >
                  <DatePicker
                    className="OrderActionContentShortTextField"
                    sx={effectiveStyles.OrderActionContentShortTextField}
                    label="Pick up Date"
                    value={dayjs(orderItemAction.actionDate || new Date())}
                    onChange={onPickupDateChange}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid
                className="OrderActionContentShortTextFieldContainer"
                sx={effectiveStyles.OrderActionContentShortTextFieldContainer}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={getMuiLocalizationProviderAdapterName()}
                >
                  <TimePicker
                    className="OrderActionContentShortTextField"
                    sx={effectiveStyles.OrderActionContentShortTextField}
                    label="Pick up Time"
                    value={dayjs(orderItemAction.actionDate || new Date())}
                    onChange={onPickupDateChange}
                    minTime={startTime}
                    maxTime={endTime}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Grid
              className="OrderActionSingleItemRowContainer"
              sx={{
                ...effectiveStyles.OrderActionSingleItemRowContainer,
                pb: 2,
              }}
            >
              <Grid
                className="OrderActionContentFullWidthTextFieldContainer"
                sx={
                  effectiveStyles.OrderActionContentFullWidthTextFieldContainer
                }
              >
                <TextField
                  className="OrderActionContentFullWidthTextField"
                  sx={effectiveStyles.OrderActionContentFullWidthTextField}
                  id="note"
                  label="Note"
                  value={orderItemAction.note || ""}
                  onChange={handleNoteValueChange}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default OrderUserSchedulePickupAction;
